import { AuthTokenStorageService } from '@/lib/auth';
import {
  CompanyContentDTO,
  CompanyDetailDTO,
  CompanyListDTO,
  FeaturedCompanyListDTO,
  GoldenCompanyListDTO,
  IFilter,
  IInsertCompanyFeedback,
  IItemResponse,
  IListResponse,
} from '@/shared';
import { CompanyListParams } from '@/features/company/types/company-list.types';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  getCompanyAwards,
  getCompanyContents,
  getCompanyDetail,
  getCompanyGroups,
  getCompanyList,
  getFeaturedCompanyList,
  getFollowedCompanies,
  getFollowedCompanyIds,
  getGoldenCompanyList,
  insertCompanyFeedback,
  omitEmptyCompanyListParams,
  toggleFollowCompany,
} from '@/features/company/api/company.api';
import { companyQueryKeys } from '@/features/company/api/company.keys';
import { userQueryKeys } from '@/features/user/api/user.keys';
import { getMutationErrorMessage } from '@/lib/api';
import { queryClient } from '@/lib/queryClient';
import { useAuthMe } from '@/features/user/api/user.queries';
import { DashboardStatsDTO } from '@/shared/user/dashboard';

export { companyQueryKeys } from '@/features/company/api/company.keys';

export const useGetCompanyFollowedCompanies = (params: IFilter = {}) => {
  const token = AuthTokenStorageService.getAccessToken();

  return useQuery({
    queryKey: companyQueryKeys.followedCompanies.list(params),
    queryFn: () => getFollowedCompanies(params),
    enabled: !!token,
  });
};

export const useGetCompanyList = (params: CompanyListParams) => {
  const cleanedParams = omitEmptyCompanyListParams(params);

  return useQuery<IListResponse<CompanyListDTO>>({
    queryKey: companyQueryKeys.list(cleanedParams),
    queryFn: () => getCompanyList(cleanedParams),
  });
};

export const useGetCompanyDetail = (alias: string) => {
  return useQuery<IItemResponse<CompanyDetailDTO>>({
    queryKey: companyQueryKeys.detail(alias),
    queryFn: () => getCompanyDetail(alias),
    enabled: !!alias,
  });
};

export const useGetFeaturedCompanyList = () => {
  return useQuery<FeaturedCompanyListDTO[]>({
    queryKey: companyQueryKeys.featuredList,
    queryFn: () => getFeaturedCompanyList(),
  });
};

export const useGetGoldenCompanyList = () => {
  return useQuery<GoldenCompanyListDTO[]>({
    queryKey: companyQueryKeys.goldenList,
    queryFn: () => getGoldenCompanyList(),
  });
};

export const useCompanyGetGroups = (groupId: number) => {
  return useQuery<IListResponse<CompanyListDTO>>({
    queryKey: companyQueryKeys.groups(groupId),
    queryFn: () => getCompanyGroups(groupId),
    enabled: !!groupId,
  });
};

export const useGetCompanyContents = (companyId: number) => {
  return useQuery<IListResponse<CompanyContentDTO>>({
    queryKey: companyQueryKeys.contents(companyId),
    queryFn: () => getCompanyContents(companyId),
    enabled: !!companyId,
  });
};

export const useFollowedCompanyIds = () => {
  const { data: user } = useAuthMe();

  return useQuery({
    enabled: !!user?.id,
    queryKey: companyQueryKeys.followedCompanyIds,
    queryFn: () => getFollowedCompanyIds(),
    staleTime: 5 * 60 * 1000,
    retryOnMount: false,
    refetchOnWindowFocus: false,
  });
};

function updateCompanyFollowerCountCache(
  companyId: number,
  delta: number
): void {
  queryClient.setQueryData<number>(
    companyQueryKeys.followerCount(companyId),
    (old) => {
      if (typeof old === 'number') {
        return Math.max(0, old + delta);
      }
      return old;
    }
  );

  queryClient.setQueriesData(
    { queryKey: companyQueryKeys.company },
    (old: unknown) => {
      if (!old || typeof old !== 'object') return old;

      if (
        'data' in old &&
        old.data &&
        typeof old.data === 'object' &&
        !Array.isArray(old.data) &&
        'id' in old.data
      ) {
        const detail = old as IItemResponse<CompanyDetailDTO>;
        const company = detail.data;
        if (!company || Number(company.id) !== companyId) return old;
        return {
          ...detail,
          data: {
            ...company,
            followerCount: Math.max(0, (company.followerCount ?? 0) + delta),
          },
        };
      }

      if ('data' in old && Array.isArray(old.data)) {
        const list = old as IListResponse<CompanyListDTO>;
        let changed = false;
        const data = list.data.map((item) => {
          if (Number(item.id) !== companyId) return item;
          changed = true;
          return {
            ...item,
            followerCount: Math.max(0, (item.followerCount ?? 0) + delta),
          };
        });
        return changed ? { ...list, data } : old;
      }

      return old;
    }
  );
}

export const useFollowCompany = () => {
  return useMutation({
    mutationFn: (companyId: number) => toggleFollowCompany(companyId),
    onSuccess: (data, companyId) => {
      const id = Number(companyId);
      const isFollowed = data.followed;
      const previous =
        queryClient.getQueryData<number[]>(
          companyQueryKeys.followedCompanyIds
        ) ?? [];
      const wasInCache = previous.some(
        (followedId) => Number(followedId) === id
      );

      queryClient.setQueryData<number[]>(
        companyQueryKeys.followedCompanyIds,
        isFollowed
          ? wasInCache
            ? previous
            : [...previous, id]
          : previous.filter((followedId) => Number(followedId) !== id)
      );

      if (!isFollowed) {
        queryClient.setQueriesData<IListResponse<CompanyListDTO>>(
          { queryKey: companyQueryKeys.followedCompanies.all },
          (old) => {
            if (!old?.data) return old;
            const exists = old.data.some(
              (company) => Number(company.id) === id
            );
            if (!exists) return old;
            return {
              ...old,
              data: old.data.filter((company) => Number(company.id) !== id),
              total:
                typeof old.total === 'number'
                  ? Math.max(0, old.total - 1)
                  : old.total,
            };
          }
        );
      }

      if (isFollowed !== wasInCache) {
        const delta = isFollowed ? 1 : -1;
        updateCompanyFollowerCountCache(id, delta);

        queryClient.setQueryData<DashboardStatsDTO>(
          userQueryKeys.dashboardStats,
          (old) => {
            if (!old) return old;
            const current = old.followedCount ?? 0;
            return {
              ...old,
              followedCount: Math.max(0, current + delta),
            };
          }
        );
      }
    },
    onError: (error: unknown) => {
      toast.error(getMutationErrorMessage(error));
    },
  });
};

export const useInsertCompanyFeedback = () => {
  return useMutation({
    mutationFn: (input: IInsertCompanyFeedback) =>
      insertCompanyFeedback(input),
  });
};

export const useCompanyAwards = (companyId: number) => {
  return useQuery({
    queryKey: companyQueryKeys.awards(companyId),
    queryFn: () => getCompanyAwards(companyId),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};
